import type { BIRevision, Preprocessing } from '@/shared/generated'

import React from 'react'
import { FeatureFlag } from '@openfeature/react-sdk'
import { any, prop } from 'lodash/fp'
import {
	CheckCircleIcon,
	CircleXIcon,
	DatabaseBackup,
	DatabaseBackupIcon,
	DatabaseIcon,
	DatabaseZapIcon,
	LoaderCircleIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useBIRevisionQuery } from '@/entities/bi'
import { usePreproQuery } from '@/entities/core'
import { useMidTermPredictionStatusQuery } from '@/entities/mid-term-prediction'

import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	SidebarMenuButton,
	SidebarMenuItem,
} from '@/shared/components/ui/sidebar'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Tag } from '@/shared/components/ui/tag'
import { cn } from '@/shared/lib/utils'
import { intlFormat } from '@/shared/tools/dates'

function isDemandEnabled(modes: string[] = []) {
	const mlModes = ['dense', 'dense_solver', 'rnn', 'rnn_solver']

	return any((m) => modes.includes(m), mlModes)
}
export default function AppSidebarIntegrationMenuItem() {
	const { t } = useTranslation()
	const preproQuery = usePreproQuery()
	const status = preproQuery.data?.status
	const demandEnabled = isDemandEnabled(preproQuery.data?.modes ?? [])

	return (
		<SidebarMenuItem>
			<Popover>
				<PopoverTrigger asChild>
					<SidebarMenuButton
						data-testid='integration-menu-item'
						className={cn('cursor-pointer', {
							'group-data-[collapsible=icon]:bg-green-100 dark:group-data-[collapsible=icon]:bg-green-950':
								status === 'finished',
							'group-data-[collapsible=icon]:bg-red-100 dark:group-data-[collapsible=icon]:bg-red-950':
								status === 'failed',
							'group-data-[collapsible=icon]:bg-orange-100 dark:group-data-[collapsible=icon]:bg-orange-950':
								status === 'started',
						})}
						tooltip={
							preproQuery.data?.date
								? `${t('prepro.date', {
										defaultValue: 'Integration date',
									})}: ${intlFormat(new Date(preproQuery.data?.date + 'Z'), {
										year: 'numeric',
										month: 'numeric',
										day: 'numeric',
										hour: 'numeric',
										minute: 'numeric',
									})}`
								: t('app.integration_status', {
										defaultValue: 'Integration status',
									})
						}
					>
						{status === 'finished' && demandEnabled && (
							<CheckCircleIcon className='rounded-full text-green-500' />
						)}
						{status === 'finished' && !demandEnabled && (
							<DatabaseIcon className='rounded-full text-green-500' />
						)}
						{status === 'failed' && (
							<DatabaseZapIcon className='rounded-full text-destructive' />
						)}
						{status === 'started' && (
							<DatabaseBackupIcon className='rounded-full text-warning' />
						)}
						{preproQuery.isPending && (
							<LoaderCircleIcon className='size-4 animate-spin' />
						)}
						<span className='truncate'>
							{t('app.integration_status', {
								defaultValue: 'Integration status',
							})}
						</span>
					</SidebarMenuButton>
				</PopoverTrigger>
				<PopoverContent className='w-auto p-0' side='right' align='end'>
					<IntegrationStatusCard />
				</PopoverContent>
			</Popover>
		</SidebarMenuItem>
	)
}

function IntegrationStatusCard() {
	const { t } = useTranslation()
	const preproQuery = usePreproQuery()
	const biRevisionDateQuery = useBIRevisionQuery<string | null>({
		refetchOnMount: 'always',
		select: prop('date') as (data: BIRevision | null) => string | null,
	})

	return (
		<div className='flex min-w-72 flex-col gap-2 p-4 text-sm'>
			<DataFieldHorizontal
				label={
					t('repricing.ml', {
						defaultValue: 'Demand-based repricing',
					}) + ':'
				}
			>
				{preproQuery.isPending ? (
					<Skeleton className='h-6 w-20' />
				) : (
					preproQuery.data &&
					(isDemandEnabled(preproQuery.data?.modes ?? []) ? (
						<Tag variant='success'>
							<CheckCircleIcon className='size-4' />
							{t('avaliable', {
								defaultValue: 'Available',
							})}
						</Tag>
					) : (
						<Tag variant='destructive'>
							<CircleXIcon className='size-4' />
							{t('not_avaliable', {
								defaultValue: 'Not abailable',
							})}
						</Tag>
					))
				)}
			</DataFieldHorizontal>
			<DataFieldHorizontal
				label={
					t('prepro.status', {
						defaultValue: 'Integration status',
					}) + ':'
				}
			>
				<div className='flex items-center gap-1 fade-in'>
					{preproQuery.isPending ? (
						<Skeleton className='h-6 w-24' />
					) : (
						preproQuery.data && (
							<DataIntegrationBage prepro={preproQuery.data} />
						)
					)}
				</div>
			</DataFieldHorizontal>
			<DataFieldHorizontal
				label={
					t('prepro.date', {
						defaultValue: 'Integration date',
					}) + ':'
				}
			>
				<div className='flex items-center gap-1 fade-in'>
					{preproQuery.isPending ? (
						<Skeleton className='h-6 w-24' />
					) : preproQuery.data?.date ? (
						intlFormat(new Date(preproQuery.data?.date + 'Z'), {
							year: 'numeric',
							month: 'numeric',
							day: 'numeric',
							hour: 'numeric',
							minute: 'numeric',
						})
					) : (
						''
					)}
				</div>
			</DataFieldHorizontal>
			<DataFieldHorizontal
				label={
					t('bi.revision.date', {
						defaultValue: 'BI revision date',
					}) + ':'
				}
			>
				{biRevisionDateQuery.isPending ? (
					<Skeleton className='h-6 w-20' />
				) : (
					biRevisionDateQuery.data && (
						<div className='flex items-center gap-1 fade-in'>
							{intlFormat(
								new Date(biRevisionDateQuery.data + 'T00:00:00.000'),
								{ year: 'numeric', month: 'numeric', day: 'numeric' }
							)}
						</div>
					)
				)}
			</DataFieldHorizontal>
			<FeatureFlag flagKey='po_midterm_predict' defaultValue={false}>
				<MidTermStatusRow />
			</FeatureFlag>
		</div>
	)
}

function MidTermStatusRow() {
	const { t } = useTranslation()
	const statusQuery = useMidTermPredictionStatusQuery()

	return (
		<DataFieldHorizontal
			data-testid='integration-midterm-status'
			label={
				t('midterm_forecast.title', { defaultValue: 'Mid-term forecast' }) + ':'
			}
		>
			{statusQuery.isPending ? (
				<Skeleton className='h-6 w-20' />
			) : statusQuery.data?.is_ready === true ? (
				<Tag variant='success' data-testid='integration-midterm-available'>
					<CheckCircleIcon className='size-4' />
					{t('avaliable', { defaultValue: 'Available' })}
				</Tag>
			) : (
				<Tag
					variant='destructive'
					data-testid='integration-midterm-unavailable'
				>
					<CircleXIcon className='size-4' />
					{t('not_avaliable', { defaultValue: 'Not available' })}
				</Tag>
			)}
		</DataFieldHorizontal>
	)
}

function DataIntegrationBage({ prepro }: { prepro: Preprocessing }) {
	const { t } = useTranslation()
	if (!prepro) return null
	const { status } = prepro
	switch (status) {
		case 'finished':
			return (
				<Tag className='flex items-center text-nowrap' variant='success'>
					<DatabaseIcon className='size-4' />
					{t('finished', {
						defaultValue: 'Finished',
					})}
				</Tag>
			)
		case 'failed':
			return (
				<Tag className='flex items-center text-nowrap' variant='destructive'>
					<DatabaseZapIcon className='size-4' />
					{t('failed', {
						defaultValue: 'Failed',
					})}
				</Tag>
			)
		case 'started':
			return (
				<Tag className='flex items-center text-nowrap' variant='warning'>
					<DatabaseBackup className='size-4' />
					{t('in_progress', {
						defaultValue: 'In progress',
					})}
				</Tag>
			)
		default:
			return null
	}
}

type DataFieldHorizontalRootProps = {
	label?: React.ReactNode
	icon?: React.ReactNode
	children?: React.ReactNode
	className?: string
} & React.HTMLAttributes<HTMLDivElement>

const DataFieldHorizontal = ({
	icon,
	label,
	children,
	className,
	...otherProps
}: DataFieldHorizontalRootProps) => (
	<div
		className={cn('flex w-full items-center gap-2', className)}
		{...otherProps}
	>
		<div className='flex w-44 flex-none items-center gap-2'>
			{icon}
			{label ? (
				<span className='line-clamp-1 shrink-0 grow basis-0 text-muted-foreground'>
					{label}
				</span>
			) : null}
		</div>
		{children ? (
			<div className='flex min-h-8 shrink-0 grow basis-0 items-center gap-2'>
				{children}
			</div>
		) : null}
	</div>
)
